import React, { useState } from 'react'
import { Spinner } from '@fluentui/react'
import type { SubformProps } from '.'
import {
  type AdditionalInfoRequirement,
  type LoanDocument,
  RequirementReasonCode,
  type SolarDoc,
  additionalInfoRequirementsSatisfied,
  hasRequiredAdditionalInformation,
} from '@oneethos/shared'
import { uploadFile, fileToObj } from '../../services/fileUploadService'
import AditionalRequirementForm from './AditionalRequirementForm'

type RequirementBucket = 'primary' | 'coBorrower'

const updateRequirementInBucket = (
  formData: SubformProps['formData'],
  update: SubformProps['update'],
  bucket: RequirementBucket,
  index: number,
  patch: Partial<AdditionalInfoRequirement>
) => {
  const prev = formData.requiredAdditionalInformation ?? {}
  const list = [...(prev[bucket] ?? [])]
  if (!list[index]) return
  list[index] = { ...list[index], ...patch }
  update({ ...formData, requiredAdditionalInformation: { ...prev, [bucket]: list } })
}

const isNameMismatchDetailComplete = (req: AdditionalInfoRequirement): boolean => {
  if (!req.reasonCodes?.includes(RequirementReasonCode.NameMismatch)) return true
  const nameMismatchDetails = req.reasonDetails?.nameMismatchDetails
  if (nameMismatchDetails == null) return false
  const { hadLifeEvent, lifeEventType, supportingDocument } = nameMismatchDetails
  if (hadLifeEvent === null) return false
  if (hadLifeEvent === false) return true
  return lifeEventType !== null && (supportingDocument?.length ?? 0) > 0
}

async function uploadRequirementDocuments(
  req: AdditionalInfoRequirement
): Promise<AdditionalInfoRequirement> {
  const docs = req.documents ?? []
  const uploaded = await Promise.all(
    docs.map(async (d) => {
      const doc = d as SolarDoc & File
      if (doc._id || doc.containerName) return d as LoanDocument
      const file = d as unknown as File
      const res = await uploadFile(file)
      return { ...fileToObj(file), ...res, docType: `additional_info_${req.type}` } as LoanDocument
    })
  )

  let reasonDetails = req.reasonDetails
  const nmd = req.reasonDetails?.nameMismatchDetails
  if (nmd?.supportingDocument?.length) {
    const uploadedSupporting = await Promise.all(
      nmd.supportingDocument.map(async (d) => {
        const doc = d as SolarDoc & File
        if (doc._id || doc.containerName) return d as LoanDocument
        const file = d as unknown as File
        const res = await uploadFile(file)
        return {
          ...fileToObj(file),
          ...res,
          docType: 'name_mismatch_supporting'
        } as LoanDocument
      })
    )
    reasonDetails = {
      ...req.reasonDetails,
      nameMismatchDetails: { ...nmd, supportingDocument: uploadedSupporting }
    }
  }

  return { ...req, documents: uploaded, reasonDetails }
}

export const AdditionalInformation = ({ formData, saving, update, save, onError }) => {
  const [submitting, setSubmitting] = useState(false)
  const req = formData.requiredAdditionalInformation
  const isJointApp = formData.isJointApp
  const primaryReqs: AdditionalInfoRequirement[] = req?.primary ?? []
  const coBorrowerReqs: AdditionalInfoRequirement[] = isJointApp ? req?.coBorrower : []

  const allReqs = isJointApp ? [...primaryReqs, ...coBorrowerReqs] : primaryReqs
  const nameMismatchDetailsComplete = allReqs.every(isNameMismatchDetailComplete)
  const canContinue =
    hasRequiredAdditionalInformation(req) &&
    additionalInfoRequirementsSatisfied(req) &&
    nameMismatchDetailsComplete

  const handleContinue = async () => {
    try {
      setSubmitting(true)
      const uploadedPrimary = await Promise.all(primaryReqs.map(uploadRequirementDocuments))
      const uploadedCo = isJointApp ? await Promise.all(coBorrowerReqs.map(uploadRequirementDocuments)) : []
      await save({
        data: {
          ...formData,
          requiredAdditionalInformation: {
            ...req,
            primary: uploadedPrimary,
            coBorrower: isJointApp && uploadedCo.length ? uploadedCo : undefined
          }
        },
        onCatch(ex: unknown) {
          const e = ex as { error?: string; message?: string }
          onError(typeof ex === 'string' ? ex : e.error || e.message || 'Save failed')
        }
      }).finally(() => setSubmitting(false))
    } catch (ex: unknown) {
      const err = ex as { message?: string; error?: string }
      onError(err.message || err.error || 'Upload failed')
      setSubmitting(false)
    }
  }

  return (
    <div>
      <h2>Additional Information Needed</h2>
      <p>To complete your application, we need a few more documents to verify your information.</p>

      {isJointApp && primaryReqs.length > 0 && <><h3>Primary borrower</h3><hr /></>}
      {primaryReqs.map((r, i) => (
        <AditionalRequirementForm
          key={`primary-${r.type}-${i}`}
          requirement={r}
          borrowerKey={`primary-${i}`}
          onDocumentsChange={(files) =>
            updateRequirementInBucket(formData, update, 'primary', i, {
              documents: files as unknown as LoanDocument[]
            })
          }
          onNameMismatchChange={(nmd) =>
            updateRequirementInBucket(formData, update, 'primary', i, {
              reasonDetails: { ...r.reasonDetails, nameMismatchDetails: nmd }
            })
          }
        />
      ))}

      {isJointApp && coBorrowerReqs.length > 0 && <><h3>Co-borrower</h3><hr /></>}
      {coBorrowerReqs.map((r, i) => (
        <AditionalRequirementForm
          key={`co-${r.type}-${i}`}
          requirement={r}
          borrowerKey={`coBorrower-${i}`}
          onDocumentsChange={(files) =>
            updateRequirementInBucket(formData, update, 'coBorrower', i, {
              documents: files as unknown as LoanDocument[]
            })
          }
          onNameMismatchChange={(nmd) =>
            updateRequirementInBucket(formData, update, 'coBorrower', i, {
              reasonDetails: { ...r.reasonDetails, nameMismatchDetails: nmd }
            })
          }
        />
      ))}

      {canContinue && (
        <div className="buttons mb-3">
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || submitting}
            onClick={handleContinue}
          >
            {saving || submitting ? <Spinner /> : 'Continue'}
          </button>
        </div>
      )}
    </div>
  )
}

export default AdditionalInformation